import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { createFieldNote, recordFieldNoteWordpressUrl } from '../../../api/admin';

const CHANNEL_LABELS = {
  wordpress: 'WordPress',
  substack: 'Substack',
  hubspot: 'HubSpot',
};

const initialFormData = {
  publication_date: '',
  title: '',
  body: '',
};

const inputClass =
  'w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600';

const FieldNotes = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [fieldNote, setFieldNote] = useState(null);
  const [wordpressUrl, setWordpressUrl] = useState('');
  const [error, setError] = useState(null);

  const generate = useMutation(createFieldNote, {
    onSuccess: (data) => {
      setError(null);
      setFieldNote(data.field_note);
      setWordpressUrl(data.field_note.wordpress_url || '');
    },
    onError: (e) => setError(e?.response?.data?.message || e.message),
  });

  const recordUrl = useMutation(
    () => recordFieldNoteWordpressUrl(fieldNote.id, wordpressUrl.trim()),
    {
      onSuccess: (data) => {
        setError(null);
        setFieldNote(data.field_note);
      },
      onError: (e) => setError(e?.response?.data?.message || e.message),
    },
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleGenerate = (event) => {
    event.preventDefault();
    generate.mutate({
      publication_date: formData.publication_date,
      title: formData.title.trim(),
      body: formData.body,
    });
  };

  const isFormValid = () =>
    formData.publication_date && formData.title.trim() && formData.body.trim();

  return (
    <div className="p-8" style={{ maxWidth: '760px' }}>
      <h1 className="text-2xl font-bold mb-2">Field Notes — Explore with ALEX</h1>
      <p className="mb-8 text-gray-700">
        Paste the proofed field note <strong>before publishing</strong>. We store it and hand
        back the per-channel &ldquo;Explore with ALEX&rdquo; links to embed in the HubSpot,
        Substack, and WordPress posts. After publishing, record the WordPress URL below.
      </p>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-100 text-red-900">{error}</div>
      )}

      {/* Step 1 — Generate */}
      <form onSubmit={handleGenerate} className="mb-8">
        <label className="block mb-1 font-medium" htmlFor="publication_date">
          Publication date
        </label>
        <input
          id="publication_date"
          name="publication_date"
          type="date"
          className={`${inputClass} mb-4`}
          value={formData.publication_date}
          onChange={handleChange}
        />

        <label className="block mb-1 font-medium" htmlFor="title">Title</label>
        <input
          id="title"
          name="title"
          type="text"
          className={`${inputClass} mb-4`}
          value={formData.title}
          onChange={handleChange}
        />

        <label className="block mb-1 font-medium" htmlFor="body">Body</label>
        <textarea
          id="body"
          name="body"
          rows={10}
          className={`${inputClass} mb-4`}
          value={formData.body}
          onChange={handleChange}
        />

        <button
          type="submit"
          disabled={!isFormValid() || generate.isLoading}
          className="bg-purple-500 text-white font-semibold rounded-lg px-5 py-3 hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {generate.isLoading ? 'Saving…' : 'Save & generate links'}
        </button>
      </form>

      {/* Result — explore URLs + step 2 */}
      {fieldNote && (
        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-lg font-bold mb-3">
            Explore links for {fieldNote.publication_date}
          </h2>
          <ul className="mb-6">
            {Object.entries(fieldNote.explore_urls || {}).map(([channel, url]) => (
              <li key={channel} className="mb-2">
                <span className="inline-block w-28 font-medium">
                  {CHANNEL_LABELS[channel] || channel}
                </span>
                <code className="text-sm break-all">{url}</code>
              </li>
            ))}
          </ul>

          <label className="block mb-1 font-medium" htmlFor="wordpress_url">
            WordPress URL{' '}
            {fieldNote.wordpress_url_pending ? (
              <span className="text-amber-700">(pending)</span>
            ) : (
              <span className="text-green-700">(recorded)</span>
            )}
          </label>
          <div className="flex gap-3">
            <input
              id="wordpress_url"
              type="url"
              className={inputClass}
              placeholder="https://admiredleadership.com/field-notes-archives/…"
              value={wordpressUrl}
              onChange={(e) => setWordpressUrl(e.target.value)}
            />
            <button
              type="button"
              onClick={() => recordUrl.mutate()}
              disabled={!wordpressUrl.trim() || recordUrl.isLoading}
              className="bg-purple-500 text-white font-semibold rounded-lg px-5 py-3 whitespace-nowrap hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {recordUrl.isLoading ? 'Saving…' : 'Record URL'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldNotes;
